/**
 * LayoutConfig
 * Configuration types for form layout customization
 */

/**
 * Section layout - groups fields into a titled section
 */
export interface SectionLayout {
    /** Section title */
    title?: string;
    /** Section description */
    description?: string;
    /** Field names to include in this section */
    fields: string[];
    /** Number of columns for fields (default: 1) */
    columns?: 1 | 2 | 3 | 4;
    /** Whether section is collapsible */
    collapsible?: boolean;
    /** Whether section starts collapsed */
    defaultCollapsed?: boolean;
    /** Custom CSS class */
    className?: string;
}

/**
 * Tab layout - groups sections into tabs
 */
export interface TabLayout {
    /** Tab label */
    label: string;
    /** Tab icon name */
    icon?: string;
    /** Sections within this tab */
    sections?: SectionLayout[];
    /** Or just field names if no sections needed */
    fields?: string[];
}

/**
 * Form layout configuration
 */
export interface FormLayoutConfig {
    /** Layout type */
    type?: 'vertical' | 'horizontal' | 'sections' | 'tabs';
    /** Sections (for sections layout) */
    sections?: SectionLayout[];
    /** Tabs (for tabs layout) */
    tabs?: TabLayout[];
    /** Default columns for all fields */
    columns?: 1 | 2 | 3 | 4;
    /** Gap between fields */
    gap?: 'sm' | 'md' | 'lg';
    /** Show field descriptions */
    showDescriptions?: boolean;
    /** Custom CSS class for form */
    className?: string;
}

/**
 * Get fields for a section
 */
export function getSectionFields(section: SectionLayout, allFields: string[]): string[] {
    return section.fields.filter((f) => allFields.includes(f));
}

/**
 * Get fields not in any section
 */
export function getUnsectionedFields(layout: FormLayoutConfig, allFields: string[]): string[] {
    const sectionedFields = new Set<string>();

    layout.sections?.forEach((section) => {
        section.fields.forEach((f) => sectionedFields.add(f));
    });

    layout.tabs?.forEach((tab) => {
        tab.fields?.forEach((f) => sectionedFields.add(f));
        tab.sections?.forEach((section) => {
            section.fields.forEach((f) => sectionedFields.add(f));
        });
    });

    return allFields.filter((f) => !sectionedFields.has(f));
}

/**
 * Default layout config
 */
export const defaultLayoutConfig: FormLayoutConfig = {
    type: 'vertical',
    columns: 1,
    gap: 'md',
    showDescriptions: true,
};
